/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { RouteConfig } from 'vue-router';
import i18n from '@/i18n';
import store from '@/features/common/store';
import { fetchAndCommitUserPrefsData } from '@/features/common/store/actions';

export const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/features/dashboard/views/Dashboard.vue'),
    meta: { title: i18n.t('pageTitle.dashboard') },
  },

  // shipment routes
  {
    path: '/shipment',
    component: () => import('@/features/shipment/views/index.vue'),
    name: 'shipment',
    redirect: { name: 'shipment-create' },
    children: [
      {
        path: 'create',
        component: () => import('@/features/shipment/views/CreateShipment.vue'),
        name: 'shipment-create',
        meta: { title: i18n.t('pageTitle.createShipment') },
      },
      {
        path: 'results',
        component: () => import('@/features/shipment/views/Results.vue'),
        name: 'shipment-results',
        meta: { title: i18n.t('pageTitle.yourResults') },
        async beforeEnter(to, from, next) {
          const availableServices = store.getters.getAvailableServices;

          const comingFromPrintOrReturn = from && ['shipment-print', 'shipment-return'].includes(from.name as string);

          const isPageReload = (!from || !from.name)
      && (!availableServices || !availableServices.length);

          if (comingFromPrintOrReturn) {
            return next({ name: 'shipment-create' });
          }

          if (isPageReload) {
            await store.dispatch('fetchAndSaveInProgressData');
            await fetchAndCommitUserPrefsData(store.commit);

            const inProgressData = store.getters.getInProgress;
            const { isValidInProgressData } = await import('@/features/shipment/helpers/createShipment');
            const { valid } = isValidInProgressData(inProgressData);

            if (valid) {
              return next();
            }

            return next({ name: 'shipment-create' });
          }

          return next();
        },
      },
      {
        path: 'customs',
        component: () => import('@/features/shipment/views/CommercialInvoice.vue'),
        name: 'shipment-customs',
        meta: { title: i18n.t('pageTitle.commercialInvoice') },
      },
      {
        path: 'checkout',
        component: () => import('@/features/shipment/views/Checkout.vue'),
        name: 'shipment-checkout',
        meta: { title: i18n.t('pageTitle.checkout') },
        async beforeEnter(to, from, next) {
          const selectedService = store.getters.getSelectedService;

          if (selectedService) {
            next();
            return;
          }

          const isFrom3dsWithError = to.query?.from === '3ds' && to.query?.errorMessage;
          if (isFrom3dsWithError) {
            await store.dispatch('fetchAndSaveInProgressData');
            next();
            return;
          }

          const isPageReload = !from || !from.name;

          if (isPageReload) {
            await store.dispatch('fetchAndSaveInProgressData');
            await fetchAndCommitUserPrefsData(store.commit);

            const inProgressData = store.getters.getInProgress;
            const { isValidInProgressData } = await import('@/features/shipment/helpers/createShipment');
            const { valid } = isValidInProgressData(inProgressData);

            if (valid && inProgressData?.serviceLevel) {
              next();
              return;
            }
          }

          next({ name: 'shipment-results' });
        },
        children: [
          {
            path: 'verify',
            component: () => import('@/features/shipment/views/Verify.vue'),
            name: 'shipment-verify',
            meta: { title: i18n.t('pageTitle.verify') },
          },
        ],
      },
      {
        path: 'print',
        component: () => import('@/features/shipment/views/Print.vue'),
        name: 'shipment-print',
        meta: { title: i18n.t('pageTitle.print') },
      },
      {
        path: 'return',
        component: () => import('@/features/shipment/views/Return.vue'),
        name: 'shipment-return',
        meta: { title: i18n.t('pageTitle.print') },
      },
      {
        path: 'success',
        component: () => import('@/features/shipment/views/ShipmentSuccess.vue'),
        name: 'shipment-success',
        meta: { title: i18n.t('pageTitle.shippingForSuccess') },
      },
    ],
  },

  // estimate routes
  {
    path: '/estimate',
    name: 'estimate',
    component: () => import('@/features/estimate/views/index.vue'),
    redirect: { name: 'estimate-get-estimate' },
    children: [
      {
        path: 'get-estimate',
        component: () => import('@/features/estimate/views/GetEstimate.vue'),
        name: 'estimate-get-estimate',
        meta: { title: i18n.t('pageTitle.getEstimate') },
      },
      {
        path: 'results',
        component: () => import('@/features/estimate/views/EstimateResults.vue'),
        name: 'estimate-results',
        meta: { title: i18n.t('pageTitle.yourResults') },
        beforeEnter(to, from, next) {
          const availableServices = store.getters.getAvailableServices;

          const isRedirectToCreate = ((!from || !from.name)
            && (!availableServices || !availableServices.length));

          if (isRedirectToCreate) {
            next({ name: 'estimate' });
          } else {
            next();
          }
        },
      },
    ],
  },

  // shipment history routes
  {
    path: '/history',
    name: 'history-results',
    meta: { title: i18n.t('pageTitle.history') },
    component: () => import('@/features/shipment-history/views/HistoryResults.vue'),
    beforeEnter(to, _, next) {
      if (!to.query.status) {
        next({ path: to.path, query: { status: 'all' } });
      } else {
        next();
      }
    },
  },
  {
    path: '/history/details',
    name: 'history-details',
    meta: { title: i18n.t('pageTitle.historyDetails') },
    component: () => import('@/features/shipment-history/views/HistoryDetails.vue'),
  },

  // pickups routes
  {
    path: '/pickups',
    name: 'pickups',
    redirect: { name: 'pickup-schedule' },
  },
  {
    path: '/pickups/schedule',
    component: () => import('@/features/schedule-pickup/views/Schedule.vue'),
    name: 'pickup-schedule',
    meta: { title: i18n.t('pageTitle.schedulePickup') },
  },
  {
    path: '/pickups/schedule/confirm',
    component: () => import('@/features/schedule-pickup/views/PickupConfirm.vue'),
    name: 'pickup-schedule-confirm',
    meta: { title: i18n.t('pageTitle.schedulePickupConfirm') },
  },
  {
    path: '/pickups/schedule/dropoff-requested',
    component: () => import('@/features/schedule-pickup/views/DropoffRequested.vue'),
    name: 'dropoff-requested-confirm',
    meta: { title: i18n.t('pageTitle.schedulePickupConfirm') },
  },
  {
    path: '/pickups/history',
    component: () => import('@/features/pickup-history/views/History.vue'),
    name: 'pickup-history',
    meta: { title: i18n.t('pageTitle.pickupHistory') },
  },
  {
    path: '/pickups/cutoff-times',
    component: () => import('@/features/cutoff-times/views/CutoffTimes.vue'),
    name: 'cutoff-times',
    meta: { title: i18n.t('pageTitle.cutoffTimes') },
  },

  // connect routes
  {
    path: '/connect',
    name: 'connect',
    redirect: { name: 'api-keys' },
  },
  {
    path: '/connect/api-keys',
    name: 'api-keys',
    meta: { title: i18n.t('pageTitle.apiKeys') },
    component: () => import('@/features/api-keys/views/ApiKeys.vue'),
  },
  {
    path: '/connect/carrier-accounts',
    name: 'carrier-accounts',
    meta: { title: i18n.t('pageTitle.carrierAccounts') },
    component: () => import('@/features/carrier-accounts/views/CarrierAccounts.vue'),
    children: [
      {
        path: 'add',
        name: 'carrier-accounts-add',
        meta: { title: i18n.t('pageTitle.addCarrierAccount') },
        component: () => import('@/features/carrier-accounts/views/AddCarrierAccount.vue'),
      },
      {
        path: 'edit',
        name: 'carrier-accounts-edit',
        meta: { title: i18n.t('pageTitle.editCarrierAccount') },
        component: () => import('@/features/carrier-accounts/views/AddCarrierAccount.vue'),
      },
      {
        path: 'cpc',
        name: 'carrier-accounts-cpc',
        meta: { title: i18n.t('pageTitle.cpcCarrierAccount') },
        component: () => import('@/features/carrier-accounts/views/CpcMyAccountIntegration.vue'),
      },
      {
        path: 'ups',
        name: 'carrier-accounts-ups',
        meta: { title: i18n.t('pageTitle.upsCarrierAccount') },
        component: () => import('@/features/carrier-accounts/views/UpsMyAccountIntegration.vue'),
      },
      {
        path: 'fedex',
        name: 'carrier-accounts-fedex',
        meta: { title: i18n.t('pageTitle.fedexRegistration') },
        component: () => import('@/features/carrier-accounts/views/FedExRegistration.vue'),
      },
    ],
  },
  {
    path: '/connect/ecommerce',
    name: 'ecommerce',
    component: () => import('@/features/ecommerce/views/ECommerce.vue'),
    meta: { title: i18n.t('pageTitle.eCommerce') },
  },
  {
    path: '/connect/ecommerce/callback/etsy',
    name: 'ecommerce-callback-etsy',
    meta: { title: i18n.t('pageTitle.ecommerceCallbacks.etsy') },
    component: () => import('@/features/ecommerce/views/callbacks/CallbackEtsy.vue'),
  },
  {
    path: '/connect/ecommerce/callback/shopify',
    name: 'ecommerce-callback-shopify',
    meta: { title: i18n.t('pageTitle.ecommerceCallbacks.shopify') },
    component: () => import('@/features/ecommerce/views/callbacks/CallbackShopify.vue'),
  },
  {
    path: '/connect/ecommerce/callback/ebay',
    name: 'ecommerce-callback-ebay',
    meta: { title: i18n.t('pageTitle.ecommerceCallbacks.ebay') },
    component: () => import('@/features/ecommerce/views/callbacks/CallbackEbay.vue'),
  },
  {
    path: '/connect/ecommerce/callback/squarespace',
    name: 'ecommerce-callback-squarespace',
    meta: { title: i18n.t('pageTitle.ecommerceCallbacks.squarespace') },
    component: () => import('@/features/ecommerce/views/callbacks/CallbackSquarespace.vue'),
  },
  {
    path: '/connect/ecommerce/callback/woocommerce',
    name: 'ecommerce-callback-woocommerce',
    meta: { title: i18n.t('pageTitle.ecommerceCallbacks.woocommerce') },
    component: () => import('@/features/ecommerce/views/callbacks/CallbackWooCommerce.vue'),
  },
  {
    path: '/connect/query-string',
    name: 'query-string',
    meta: { title: i18n.t('pageTitle.queryString') },
    component: () => import('@/features/query-string/views/QueryString.vue'),
    children: [
      {
        path: 'get-estimate',
        name: 'query-string-estimate',
        meta: { title: i18n.t('pageTitle.queryString') },
      },
      {
        path: 'create-shipment',
        name: 'query-string-shipment',
        meta: { title: i18n.t('pageTitle.queryString') },
      },
    ],
  },

  // settings routes
  {
    path: '/settings',
    name: 'settings',
    redirect: { name: 'settings-preferences' },
  },
  {
    path: '/settings/preferences',
    name: 'settings-preferences',
    component: () => import('@/features/settings/views/Preferences.vue'),
    meta: { title: i18n.t('pageTitle.preferences') },
    beforeEnter(to, _, next) {
      if (!to.query.content) {
        next({ path: to.path, query: { content: 'shippingSettings' } });
      } else {
        next();
      }
    },
  },
  {
    path: '/settings/notifications',
    name: 'settings-notifications',
    component: () => import('@/features/settings/views/Notifications.vue'),
    meta: { title: i18n.t('pageTitle.notifications') },
    beforeEnter(to, _, next) {
      if (!to.query.content) {
        next({ path: to.path, query: { content: 'shipments' } });
      } else {
        next();
      }
    },
  },

  // account routes
  {
    path: '/account',
    name: 'account',
    redirect: { name: 'account-users' },
  },
  {
    path: '/account/users',
    component: () => import('@/features/users/views/Users.vue'),
    name: 'account-users',
    meta: { title: i18n.t('pageTitle.users') },
  },
  {
    path: '/account/invoices',
    component: () => import('@/features/invoices/views/Invoices.vue'),
    name: 'account-invoices',
    meta: { title: i18n.t('pageTitle.invoices') },
  },
  {
    path: '/account/change-password',
    name: 'change-password',
    meta: { title: i18n.t('pageTitle.changePassword') },
    component: () => import('@/features/auth/views/ChangePassword.vue'),
  },
  {
    path: '/account/set-password',
    name: 'set-password',
    meta: { title: i18n.t('pageTitle.setPassword') },
    component: () => import('@/features/auth/views/SetPassword.vue'),
    async beforeEnter(to, from, next) {
      const { isFeatureEnabled } = await import('@/features/common/helpers/feature-flag');
      const isV3 = await isFeatureEnabled('feature-v3-change-password');
      if (isV3) {
        const { redirectTo } = await import('@/features/common/helpers');
        const query = to.query.type ? `?type=${to.query.type}` : '';
        redirectTo(`/ship2/account/set-password${query}`);
        return;
      }
      next();
    },
  },
  {
    path: '/account/payment-methods',
    name: 'payment-methods',
    meta: { title: i18n.t('pageTitle.paymentMethods') },
    component: () => import('@/features/payment-methods/views/PaymentMethods.vue'),
  },

  // other routes
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/features/auth/views/SignIn.vue'),
    meta: { title: i18n.t('pageTitle.signIn') },
  },
  {
    path: '/sign-in/confirm-email',
    name: 'sign-in-confirm-email',
    component: () => import('@/features/auth/views/SignInEmailConfirm.vue'),
    meta: { title: i18n.t('pageTitle.signIn') },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: { title: i18n.t('pageTitle.signOut') },
    component: () => import('@/features/auth/views/Logout.vue'),
    beforeEnter: (_, __, next) => {
      store.dispatch('logout');
      next();
    },
  },
  {
    path: '/account-disabled',
    name: 'account-disabled',
    meta: { title: i18n.t('pageTitle.accountDisabled') },
    component: () => import('@/features/auth/views/AccountDisabled.vue'),
  },
  {
    path: '/account-deleted',
    name: 'account-deleted',
    meta: { title: i18n.t('pageTitle.accountDeleted') },
    component: () => import('@/features/auth/views/AccountDeleted.vue'),
  },
  {
    path: '/terms',
    name: 'terms',
    meta: { title: i18n.t('pageTitle.terms') },
    component: () => import('@/features/terms-and-conditions/views/Terms.vue'),
  },
  {
    path: '/user',
    name: 'user',
    redirect: { name: 'verify-user' },
  },
  {
    path: '/user/verify-user', // user invitation link from email
    name: 'verify-user',
    meta: { title: i18n.t('pageTitle.verifyUser') },
    component: () => import('@/features/auth/views/user/VerifyUser.vue'),
  },
  {
    path: '/user/verify-password',
    name: 'verify-password',
    meta: { title: i18n.t('pageTitle.verifyPassword') },
    component: () => import('@/features/auth/views/user/VerifyPassword.vue'),
  },
  {
    path: '/user/password', // forgot password - reset password link from email
    name: 'verify-password-reset',
    meta: { title: i18n.t('pageTitle.verifyPassword') },
    component: () => import('@/features/auth/views/user/Password.vue'),
  },
  {
    path: '/X7k2M9p4Zq8YvT6WsJ3LnBdC5RfGoQ1UhN0/simulation', // simulation page
    name: 'features-simulation',
    meta: { title: 'Simulation of various features' },
    component: () => import('@/features/simulation/views/index.vue'),
  },
];
